import React from 'react';
import { BarChart, Trash2, Layers, Database } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных проектов
 */
const SavedProjectsSidebar = ({ 
  savedProjects, 
  deleteSavedProject, 
  loadSavedProject 
}) => {
  // Если нет сохраненных проектов
  if (!savedProjects || savedProjects.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Database className="w-5 h-5 mr-2 text-indigo-500" />
          Сохраненные проекты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные проекты для сравнения и повторного использования.
        </p>
      </div>
    );
  }

  // Вспомогательная функция для форматирования валюты
  const formatCurrencyWithSymbol = (value, currencyCode) => {
    const currencySymbol = getCurrencySymbol(currencyCode);
    const formattedValue = formatNumber(value, 0);
    return `${formattedValue} ${currencySymbol}`;
  };

  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    switch ((currencyCode || '').toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'rub': return '₽';
      case 'gbp': return '£';
      default: return currencyCode || '$';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Database className="w-5 h-5 mr-2 text-indigo-500" />
          Сохраненные проекты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProjects.length} {pluralizeProjects(savedProjects.length)}
        </span>
      </div>

      <div className="space-y-4">
        {savedProjects.map((project) => (
          <div 
            key={project.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {project.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedProject(project.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProject(project.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Объем данных:</span>
                <span className="font-medium">
                  {getFormattedDataVolume(project.formData.dataVolume, project.formData.dataVolumeUnit)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {project.formData.projectDuration} {pluralizeMonths(parseInt(project.formData.projectDuration))}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип проекта:</span>
                <span>
                  {getProjectTypeName(project.formData.projectType)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Общая стоимость:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrencyWithSymbol(project.results.totalCost, project.formData.currency)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "проект"
const pluralizeProjects = (count) => {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'проект';
  } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
    return 'проекта';
  } else {
    return 'проектов';
  }
};

// Вспомогательная функция для склонения слова "месяц"
const pluralizeMonths = (count) => {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'месяц';
  } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
    return 'месяца';
  } else {
    return 'месяцев';
  }
};

// Форматирование объема данных
const getFormattedDataVolume = (volume, unit) => {
  const numVolume = parseFloat(volume);
  if (isNaN(numVolume)) return '0';
  
  switch (unit) {
    case 'gb':
      if (numVolume >= 1024) return `${(numVolume / 1024).toFixed(1)} TB`;
      return `${numVolume} GB`;
    case 'tb':
      if (numVolume >= 1024) return `${(numVolume / 1024).toFixed(1)} PB`;
      return `${numVolume} TB`;
    case 'pb':
      return `${numVolume} PB`;
    default:
      return `${numVolume} ${unit.toUpperCase()}`;
  }
};

// Получение названия типа проекта
const getProjectTypeName = (projectTypeId) => {
  const projectTypes = {
    'analytics': 'Аналитика',
    'ml': 'ML',
    'realtime': 'Real-time',
    'datawarehouse': 'DW',
    'dataquality': 'Качество данных',
    'dataintegration': 'Интеграция'
  };
  
  return projectTypes[projectTypeId] || projectTypeId;
};

export default SavedProjectsSidebar;